import { Loader2, MoreVerticalIcon, PlusIcon, SearchIcon, X } from "lucide-react";
import React, { JSX, Suspense, lazy, useCallback } from "react";
import { useEffect, useState } from "react";
import queryString from "query-string";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import { Separator } from "../../components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "../../components/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import apiService from "../../services/apiService";
import {
  DragAndDropUpload,
  DragAndDropIcon,
  DragAndDropHelpText,
  DragAndDropTitle,
  DragAndDropMaxSizeText,
} from "../../pages/customer-details/components/UI";
import { toast } from "react-toastify";
import { getLocalStorageItem, getFileExtension } from "../../utils";
import axios from "axios";

interface User {
  is_active: string;
  name: string;
  email: string;
  mobile: string;
  designation_id: string;
  status: 'Active' | 'Inactive';
  initials: string;
  id: number;
}




interface PaginationData {
  currentPage: number;
  totalCustomers: number;
  pageSize: number;
  totalPages: number;
}

interface FileDetails {
  name: string;
  size: number;
  fileType: string;
}

interface PresignedUrlResponse {
  data: {
    preSignedUrl: string;
  };
}


interface Partner {
  id: number;
  name: string;
  initials: string;
  avatar: string | null;
  users: User[];
  created_at: string;
  rm_name: string;
  location: string;
  dealsReferred: number;
  product: string;
  profile: NonNullable<{
    partner_type: string;
    company_name: string;
    zone: string;
    gst_number: string;
    address_line1: string;
    address_city: string;
    address_state: string;
    address_pincode: string;
    document_pan: File | null;
    document_aadhar: File | null;
    document_gst: File | null;
    document_msme: File | null;
    payout_percentage: number;
    payout_type: string;
    agreement: File | null;
  }>;
}

interface ApiDocument {
  name: string;
  sizeInBytes: number;
  documentType: string;
  documentCategory: string;
  status: string;
  signedUrl: string;
  createdAt: string;
  updatedAt: string;
  imageUrl: string;
  s3FileKey: string; // Added property
}

interface ApiResponse {
  data: ApiDocument[];
}

interface CurrentFilter {
  page: number;
  pageSize: number;
  search?: string;
  relationshipManager?: string;
  location?: string;
  requirement?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
}

interface PostData {
  partner_id: string;
  fileDetails: FileDetails;
  documentType: string;
  documentCategory: string;
  [key: string]: unknown;
}

interface DocumentData {
  title: string;
  type: string;
  filename: string;
}

const Data: DocumentData[] = [
  {
    title: "PAN Card",
    type: "PARTNER_PAN",
    filename: "pan_card",
  },
  {
    title: "AADHAR Card",
    type: "PARTNER_AADHAR",
    filename: "aadhar_card",
  },
  {
    title: "GST",
    type: "PARTNER_GST",
    filename: "gst",
  },
  {
    title: "MSME",
    type: "PARTNER_MSME",
    filename: "msme",
  }

];

const partneraggreement: DocumentData[] = [
  {
    title: "AGGREEMENT",
    type: "PARTNER_AGGREEMENT",
    filename: "aggreement",
  }
];

interface ApiDocument {
  name: string;
  sizeInBytes: number;
  documentType: string;
  documentCategory: string;
  status: string;
  signedUrl: string;
  createdAt: string;
  updatedAt: string;
}



interface Document {
  name: string;
  sizeInBytes: number;
  fileType: string;
  documentType: string;
  documentUrl: string;
  documentCategory: string;
  status: string;
  signedUrl?: string;
  imageUrl?: string;
  s3FileKey?: string;
}

interface PartnerDesignationResponse {
  data: PartnerDesignationTypes[];
}

interface PartnerDesignationTypes {
  id: number;
  name: string;
  display_order: number;
}

const LazyUploadPreview = lazy(() =>
  import("../../pages/customer-details/components/UI/UploadPreview").then((module) => ({
    default: module.UploadPreview,
  }))
);

export const ManagePartners = (): JSX.Element => {
  const [pincodeError, setPincodeError] = useState<string | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isAddPartnerOpen, setIsAddPartnerOpen] = useState(false);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isFetchingDocuments, setIsFetchingDocuments] = useState(false);
  const [uploadingDocumentTypes, setUploadingDocumentTypes] = useState<Record<string, boolean>>({});
  const [partner_designation, setPartnerDesignation] = useState<PartnerDesignationTypes[]>([]);
  const [isUserSheetOpen, setIsUserSheetOpen] = useState(false);
  const [isProfileSheetOpen, setIsProfileSheetOpen] = useState(false);


  const zoneOptions = [
    { value: "North", label: "North" },
    { value: "South", label: "South" },
    { value: "East", label: "East" },
    { value: "West", label: "West" },
    { value: "Center", label: "Center" },
    { value: "All India", label: "All India" },
  ];

  const normalizeZoneValue = (zone: string | undefined): string => {
    if (!zone) return "";
    const firstZone = zone.split(",")[0]?.trim();
    return firstZone === "AllIndia" ? "All India" : (firstZone || "");
  };

  const ManageDate = (data: string) => {
    const rawDate = data;
    const formattedDate = new Date(rawDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).replace(",", "");

    return formattedDate; // Output: May 07 2025
  };


  const [filterTabs, setFilterTabs] = useState([
    { id: "total", label: "Total", count: 0, active: true },
    { id: "active", label: "Active", count: 0, active: false },
    { id: "inactive", label: "Inactive", count: 0, active: false },
  ]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    designation_id: "",
    mca: "",
    org: "",
    product: "",
  });

  const [addUserData, setAddUserData] = useState({
    userName: "",
    userEmail: "",
    userMobile: "",
    userDesignation_id: "",
    partner_company_id: ""
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchLenderDesignation = async () => {
      try {
        const response = await apiService.get<PartnerDesignationResponse>(`/lender/get-lender-designation-types`);
        const transformedDocs = response.data.map((doc) => ({
          name: doc.name,
          id: doc.id,
          display_order: doc.display_order,
        }));
        setPartnerDesignation(transformedDocs);
      } catch (error) {
        console.error("Error fetching Partner types:", error);
      }
    };

    fetchLenderDesignation();
  }, []);

  const handleApiCall = async (apiCall: () => Promise<any>, successMessage?: string) => {
    try {
      const response = await apiCall();
      // Check if the response indicates success
      if (response.status === 200 || response.status === "success" || response.status === 201) {
        if (successMessage) {
          toast.success(successMessage); // Optionally show a success message
        }
        return response;
      } else {
        toast("Something went wrong. Please try again.");
        // throw new Error("API call failed");
      }
    } catch (error: any) {
      console.error("API Error:", error.message); // Log the error for debugging
      if (error.response && error.response.data && error.response.data.message, error.message) {
        // Display the error message from the backend
        toast.error(error.message);
      } else {
        toast.error("An error occurred. Please try again.");
      }
      // throw error;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));

    // Clear error for this field when it changes
    if (formErrors[id]) {
      setFormErrors(prev => ({
        ...prev,
        [id]: ""
      }));
    }
  };

  const handleChangeadduser = (e: any) => {
    setAddUserData((prev) => ({
      ...prev,
      [e.target.id]: e.target.value,
    }));
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addpartner = async () => {
    // Validate required fields
    const newErrors: Record<string, string> = {};
    let isValid = true;

    // Required fields validation
    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!formData.email || formData.email.trim() === "") {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    if (!formData.product || formData.product.trim() === "") {
      newErrors.product = "Product is required";
      isValid = false;
    }

    if (!formData.mca || formData.mca.trim() === "") {
      newErrors.mca = "MCA Registered Entity Name is required";
      isValid = false;
    }

    // Update errors state
    setFormErrors(newErrors);

    // If validation fails, stop here
    if (!isValid) {
      return;
    }

    try {
      const result = await handleApiCall(
        () =>
          apiService.post("/partner/create-partner", {
            ...formData,
          }),
        "Partner added successfully!"
      );

      if (!result) {
        return;
      }

      setFormData({
        name: "",
        email: "",
        mobile: "",
        designation_id: "",
        mca: "",
        org: "",
        product: "",
      });
      setIsAddPartnerOpen(false);

      await fetchPageDataWithFilters();
    } catch (error) {
      console.error("addpartner:", error);
    }
  };

  const handleViewProfile = (partner: Partner, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedPartner(partner);
    setIsProfileSheetOpen(true);
  };

  const handlePartnerClick = (partner: Partner) => {
    setSelectedPartner(partner);
    setIsUserSheetOpen(true);
  };

  useEffect(() => {
    if (isProfileSheetOpen && selectedPartner?.id) {
      fetchALlDocuments();
    }
  }, [isProfileSheetOpen, selectedPartner?.id]);

  useEffect(() => {
    if (!isProfileSheetOpen || !isUserSheetOpen) {
      document.body.removeAttribute('data-scroll-locked');
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
    }

  }, [isProfileSheetOpen, isUserSheetOpen]);

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditUserOpen(true);
  };

  const getDocument = (
    type: string,
    documents: Document[] = []
  ): Document | undefined => {
    return documents.find((document) => document.documentType === type);
  };

  const handleDeactivateUser = async (user: User) => {
    if (!selectedPartner) return;

    try {
      await handleApiCall(
        () =>
          apiService.post("/partner/deactivate-user", {
            id: user.id,
          }),
        "User deactivated successfully!"
      );

      // Update the state
      const updatedUsers = selectedPartner.users.map((u) =>
        u.id === user.id ? { ...u, is_active: "0" } : u
      );
      setSelectedPartner({
        ...selectedPartner,
        users: updatedUsers,
      });
      setCustomers((prevPartners) =>
        prevPartners.map((partner) =>
          partner.id === selectedPartner.id
            ? { ...partner, users: updatedUsers }
            : partner
        )
      );
    } catch (error) {
    }
  };

  const handleUpdateUser = async (updatedUser: User) => {
    if (!selectedPartner || !selectedUser) {
      toast.error("No partner or user selected.");
      return;
    }

    try {
      // Make the API call to update the user
      await handleApiCall(
        () =>
          apiService.post("/partner/update-partner-user", {
            ...updatedUser,
            partner_company_id: String(selectedPartner.id),
          }),
        "User updated successfully!"
      );

      // Update the local state for the selected partner
      const updatedUsers = selectedPartner.users.map((user) =>
        user.id === selectedUser.id ? updatedUser : user
      );

      setSelectedPartner({
        ...selectedPartner,
        users: updatedUsers,
      });

      // Refresh the data
      await fetchPageDataWithFilters();

      // Close the edit user modal
      setIsEditUserOpen(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error("An error occurred while updating the user. Please try again.");
    }
  };

  const handleActivateUser = async (user: User) => {
    if (!selectedPartner) return;

    try {
      await handleApiCall(
        () =>
          apiService.post("/lender/activate-user", {
            id: user.id,
          }),
        "User activated successfully!"
      );

      // Update the state
      const updatedUsers = selectedPartner.users.map((u) =>
        u.id === user.id ? { ...u, is_active: "1" } : u
      );
      setSelectedPartner({
        ...selectedPartner,
        users: updatedUsers,
      });
      setCustomers((prevPartners) =>
        prevPartners.map((Partner) =>
          Partner.id === selectedPartner.id
            ? { ...Partner, users: updatedUsers }
            : Partner
        )
      );
    } catch (error) {
    }
  };

  const [partners, setCustomers] = useState<Partner[]>([]);

  const [paginationData, setPaginationData] = useState<PaginationData>({
    currentPage: 1,
    totalCustomers: 0,
    pageSize: 25,
    totalPages: 1,
  });



  const [currentFilter, setCurrentFilter] = useState<CurrentFilter>({
    page: 1,
    pageSize: 25,
  });



  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchPageDataWithFilters();
    }, 500); // Wait 500ms after user stops typing
  
    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const fetchPageDataWithFilters = async () => {
    try {
      let filter ={
        ...currentFilter,
        search : searchQuery
      };
      const response = await handleApiCall(
        () =>
          apiService.get<{
            customers: Partner[];
            pagination: PaginationData;
          }>(`/partner/fetchAllCustomers?${queryString.stringify(filter)}`)
      );


      // Update the state with the fetched data
      setCustomers(response.data.customers);
      setPaginationData(response.data.pagination);
      setFilterTabs((prevTabs) =>
        prevTabs.map((tab) =>
          tab.id === "total"
            ? { ...tab, count: response.data.pagination.totalCustomers }
            : tab
        )
      );

    } catch (error) {
      toast.error("An error occurred while fetching data. Please try again.");
    }
  };

  const addpartneruser = async () => {
    if (!selectedPartner?.id) {
      toast.error("No partner selected.");
      return;
    }

    try {
      const response = await handleApiCall(
        () =>
          apiService.post("/partner/create-partner-user", {
            ...addUserData,
            partner_company_id: String(selectedPartner.id),
          }),
        "User added successfully!"
      );

      const newUser = response.userdetails;

      // Reset the form and close the modal
      setAddUserData({
        userName: "",
        userEmail: "",
        userMobile: "",
        userDesignation_id: "",
        partner_company_id: "",
      });
      setIsAddUserOpen(false);

      // Update the state
      setSelectedPartner((prev) =>
        prev
          ? {
            ...prev,
            users: [newUser, ...prev.users], // Add the new user at the beginning
          }
          : null
      );
      setCustomers((prevPartners) =>
        prevPartners.map((partner) =>
          partner.id === selectedPartner.id
            ? { ...partner, users: [newUser, ...partner.users] } // Add the new user at the beginning
            : partner
        )
      );
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchPageDataWithFilters();
  }, [currentFilter]);

  const fetchALlDocuments = useCallback(() => {
    if (!selectedPartner?.id) return;
    setIsFetchingDocuments(true);

    apiService
      .get<ApiResponse>(`/partner/company/${selectedPartner.id}/documents`)
      .then((response) => {

        const transformedDocs = response.data.map((doc) => ({
          name: doc.name,
          sizeInBytes: doc.sizeInBytes,
          fileType: doc.name.split(".").pop() || "",
          documentType: doc.documentType,
          documentUrl: doc.imageUrl,
          documentCategory: doc.documentCategory,
          status: doc.status,
          signedUrl: doc.signedUrl,
          imageUrl: doc.imageUrl,
          s3FileKey: doc.s3FileKey,
        }));
        setDocuments(transformedDocs);
      })
      .catch(() => {
      })
      .finally(() => {
        setIsFetchingDocuments(false);
      });
  }, [selectedPartner?.id]);


  const onDrop = useCallback(
    async (acceptedFiles: File[], documentType: string, fileName?: string) => {
      const documentCategory = "PARTNER_KYC"; // Adjust this if needed

      setUploadingDocumentTypes((prev) => ({ ...prev, [documentType]: true }));
      try {
        for (const file of acceptedFiles) {
          const fileDetails: FileDetails = {
            name: fileName
              ? `${fileName}.${getFileExtension(file.type)}`
              : file.name,
            size: file.size,
            fileType: file.type,
          };

          const postData: PostData = {
            partner_id: String(selectedPartner?.id) || "",
            fileDetails,
            documentType,
            documentCategory,
          };

          // Generate presigned URL and upload the file
          const response = await apiService.post<PresignedUrlResponse>(
            "/partner/generatePresignedUrl",
            postData
          );

          await axios.put(response.data.preSignedUrl, file, {
            headers: {
              "Content-Type": file.type,
            },
          });

          // Confirm the document upload
          const pathBody = {
            partner_id: selectedPartner?.id,
            fileMetadata: {
              documentType,
              documentCategory,
              status: "UPLOADED_TO_S3",
            },
            loggedInUser: getLocalStorageItem("detail")?.userId,
          };

          await apiService.patch("/partner/confirmDocumentUpload", pathBody);
        }

        // Refresh the document list
        if (selectedPartner?.id) {
          fetchALlDocuments();
        }

        toast.success("Documents uploaded successfully!");
      } catch (error) {
        toast.error("An error occurred while uploading documents. Please try again.");
      } finally {
        setUploadingDocumentTypes((prev) => ({ ...prev, [documentType]: false }));
      }
    },
    [selectedPartner?.id, fetchALlDocuments]
  );

  const getInitials = (name: string): string => {
    if (!name || typeof name !== "string") return ""; // Handle empty or invalid names
    const words = name.trim().split(/\s+/); // Split by spaces and remove extra spaces
    return words.slice(0, 2).map(word => word[0].toUpperCase()).join(""); // Get first letters of the first two words
  };

  const handlePartnerTypeChange = (value: string) => {
    if (selectedPartner?.profile) {
      setSelectedPartner({
        ...selectedPartner,
        profile: {
          ...selectedPartner.profile,
          partner_type: value,
        },
      });
    }
  };

  const handleZoneChange = (value: string) => {
    if (selectedPartner?.profile) {
      setSelectedPartner({
        ...selectedPartner,
        profile: {
          ...selectedPartner.profile,
          zone: value,
        },
      });
    }
  };

  const handlePayoutChange = (value: string) => {
    if (selectedPartner?.profile) {
      setSelectedPartner({
        ...selectedPartner,
        profile: {
          ...selectedPartner.profile,
          payout_type: value,
        },
      });
    }
  };

  const handleProfileInputChange = (field: keyof Partner['profile'], value: string) => {
    if (field === 'address_pincode') {
      const pincodeRegex = /^[1-9][0-9]{5}$/; // Regex for a valid 6-digit pincode
      if (!pincodeRegex.test(value)) {
        setPincodeError("Invalid pincode. Please enter a valid 6-digit number.");
      } else {
        setPincodeError(null); // Clear the error if valid
      }
    }

    if (selectedPartner?.profile) {
      setSelectedPartner({
        ...selectedPartner,
        profile: {
          ...selectedPartner.profile,
          [field]: value,
        },
      });
    }
  };

  const handledisplayname = (value: string) => {
    if (selectedPartner?.profile) {
      setSelectedPartner({
        ...selectedPartner,
        name: value,
      });
    }
  };

  const saveProfileChanges = async () => {
    if (!selectedPartner?.profile) return;
    try {
      await handleApiCall(
        () =>
          apiService.post(`/partner/update-profile`, {
            id: selectedPartner.id,
            name: selectedPartner.name,
            profile: selectedPartner.profile,
          }),
        "Profile updated successfully!"
      );
      setIsProfileSheetOpen(false);
      // Refresh the lender list
      await fetchPageDataWithFilters();

      // Optionally close the profile sidebar
      // setIsProfileOpen(false);
    } catch (error) {
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentFilter((prev) => ({
      ...prev,
      page: newPage, // Update the current page
    }));
  };

  const handleAddUserOpen = (isOpen: boolean) => {
    setIsAddUserOpen(isOpen);
    if (isOpen) {
      setAddUserData({
        userName: "",
        userEmail: "",
        userMobile: "",
        userDesignation_id: "",
        partner_company_id: "",
      });
    }
  };

  const handleAddPartnerOpen = (isOpen: boolean) => {
    setIsAddPartnerOpen(isOpen);
    if (isOpen) {
      setFormData({
        name: "",
        email: "",
        mobile: "",
        designation_id: "",
        mca: "",
        org: "",
        product: "",
      });
    }
  };

  return (
    <div className="bg-white flex flex-row justify-center w-full">
      <div className="bg-white w-full max-w-[1440px] p-6">
        <div className="w-full max-w-[1376px] mx-auto">
          <div className="flex items-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-800 font-['Poppins',Helvetica]">
              Partners
            </h1>

            <Separator orientation="vertical" className="mx-4 h-[55px]" />

            <div className="flex items-center space-x-1">
              <ToggleGroup
                type="single"
                defaultValue="total"
                className="border border-neutral-200 rounded-lg p-1"
              >
                {filterTabs.map((tab) => (
                  <ToggleGroupItem
                    key={tab.id}
                    value={tab.id}
                    className={`w-[94px] rounded-lg whitespace-nowrap ${tab.active ? "bg-[#3f8b9833] text-[#3f8b98]" : ""
                      }`}
                  >
                    <span className="font-['Poppins',Helvetica] font-medium text-base">
                      {tab.label} ({tab.count})
                    </span>
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>

            <div className="ml-auto flex items-center gap-2">
              {isSearchExpanded ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    placeholder="Search partners or users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-[300px] "
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setIsSearchExpanded(false);
                      setSearchQuery("");
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-xl border-[#3f8b98]"
                  onClick={() => setIsSearchExpanded(true)}
                >
                  <SearchIcon className="h-4 w-4" />
                </Button>
              )}
              <Button
                className="rounded-xl bg-[#3f8b98] text-white font-['Poppins',Helvetica] font-medium hover:bg-[#357a85] cursor-pointer"
                onClick={() => handleAddPartnerOpen(true)}
              >
                <PlusIcon className="mr-2 h-3.5 w-3.5" />
                Add Partner
              </Button>
            </div>
          </div>

          <Card className="rounded-2xl border-neutral-200 shadow-[0px_1px_2px_#0000000d]">
            <CardContent className="p-0">
              <Table >
                <TableHeader>
                  <TableRow className="border-b border-neutral-200">
                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[250px]">
                      Partner Name
                    </TableHead>
                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[180px]">
                      User Name
                    </TableHead>
                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[150px]">
                      Created On
                    </TableHead>
                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[180px]">
                      RM Name
                    </TableHead>
                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[200px]">
                      Location
                    </TableHead>
                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[120px]">
                      Product
                    </TableHead>
                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[120px]">
                      Deals Referred
                    </TableHead>
                    <TableHead className="font-bold text-gray-500 text-sm font-['Poppins',Helvetica] py-4 pl-6 w-[100px]">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partners.map((partner) => (
                    <TableRow
                      key={partner.id}
                      className="border-b border-neutral-200 cursor-pointer hover:bg-gray-50"
                      onClick={() => handlePartnerClick(partner)}
                    >
                      <TableCell className="py-4 pl-6">
                        <div className="flex items-center">
                          {partner.avatar ? (
                            <Avatar className="h-8 w-8 rounded-lg">
                              <AvatarImage src={partner.avatar} alt={partner.name} />
                              <AvatarFallback>{getInitials(partner.name)}</AvatarFallback>

                            </Avatar>
                          ) : (
                            <div className="h-8 w-8 rounded-lg bg-[#8c650033] flex items-center justify-center">
                              <span className="text-sm">{getInitials(partner.name)}</span>
                            </div>
                          )}
                          <span className="ml-3 font-['Poppins',Helvetica] text-gray-800 text-base">
                            {partner.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 pl-6 font-['Poppins',Helvetica] text-base">
                        <span className="text-gray-600">{partner.users[0].name}</span>
                        {partner.users.length > 1 && (
                          <>
                            <span className="text-gray-600">, </span>
                            <span className="font-medium text-[#3f8b98]">
                              +{partner.users.length - 1} more
                            </span>
                          </>
                        )}
                      </TableCell>
                      <TableCell className="py-4 pl-6 font-['Poppins',Helvetica] text-gray-600 text-base">

                        {ManageDate(partner.created_at)}
                      </TableCell>
                      <TableCell className="py-4 pl-6 font-['Poppins',Helvetica] text-gray-600 text-base">
                        {partner.rm_name}
                      </TableCell>
                      <TableCell className="py-4 pl-6 font-['Poppins',Helvetica] text-gray-600 text-base">
                        {partner.location ? partner.location : 'N/A'}
                      </TableCell>
                      <TableCell className="py-4 pl-6">


                        <Badge
                          variant="outline"
                          className={`rounded-full bg-${partner.product === "Hybrid" ? "[#3f8b981a]" : partner.product === "Hybrid" ? "[#8c6500]" : "[#8c65001a]"
                            } text-${partner.product === "Hybrid" ? "[#3f8b98]" : partner.product === "Hybrid" ? "[#8c6500]" : "[#808080]"
                            } border-0 px-3 py-1 font-normal text-sm`}
                        >

                          {partner?.product === "Hybrid" || partner?.product === "Leasing"
                            ? partner.product
                            : "-"}
                        </Badge>


                      </TableCell>
                      <TableCell className="py-4 pl-6 font-['Poppins',Helvetica] text-gray-600 text-base">
                        {partner.dealsReferred ? partner.dealsReferred : 0}
                      </TableCell>
                      <TableCell className="py-4 pl-6">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 p-0"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreVerticalIcon className="h-4 w-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => {
                              setSelectedPartner(partner);
                              setIsUserSheetOpen(true);
                            }}>
                              Manage User
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e: any) => handleViewProfile(partner, e)}>
                              View Profile
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <div className="text-sm text-gray-500 font-['Poppins',Helvetica]">
                  {paginationData.totalCustomers === 0 ? (
                    "Showing 0 of 0 entries"
                  ) : (
                    <>
                      Showing {(paginationData.currentPage - 1) * paginationData.pageSize + 1} to{" "}
                      {Math.min(
                        paginationData.currentPage * paginationData.pageSize,
                        paginationData.totalCustomers
                      )}{" "}
                      of {paginationData.totalCustomers} entries
                    </>
                  )}
                </div>
                <div className="ml-auto">
                  <Pagination>
                    <PaginationContent>
                      {/* Previous Button */}
                      <PaginationItem>
                        <PaginationPrevious
                          className="border-[#3f8b98] text-[#3f8b98] border rounded-lg h-[34px] w-24 font-['Poppins',Helvetica] font-normal"
                          onClick={() =>
                            paginationData.currentPage > 1 &&
                            handlePageChange(paginationData.currentPage - 1)
                          }
                        >
                          Previous
                        </PaginationPrevious>
                      </PaginationItem>

                      {/* Page Numbers */}
                      {Array.from({ length: paginationData.totalPages }, (_, index) => (
                        <PaginationItem key={index}>
                          <PaginationLink
                            className={`rounded-lg h-8 w-[30px] ${paginationData.currentPage === index + 1
                              ? "bg-[#3f8b98] text-white" // Active page styling
                              : "border-[#3f8b98] text-[#3f8b98] border" // Inactive page styling
                              } font-['Poppins',Helvetica] font-normal`}
                            onClick={() => handlePageChange(index + 1)}
                          >
                            <span className="text-red"> {index + 1}

                            </span>
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      {/* Next Button */}
                      <PaginationItem>
                        <PaginationNext
                          className="border-[#3f8b98] text-[#3f8b98] border rounded-lg h-[34px] w-16 font-['Poppins',Helvetica] font-normal"
                          onClick={() =>
                            paginationData.currentPage < paginationData.totalPages &&
                            handlePageChange(paginationData.currentPage + 1)
                          }
                        >
                          Next
                        </PaginationNext>
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>

      {/* Partner Details Sidebar */}
      <Sheet open={isUserSheetOpen} onOpenChange={setIsUserSheetOpen}>
        <SheetContent className="w-fit overflow-y-auto">
          <SheetHeader className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <SheetTitle className="text-xl font-semibold">Manage Users</SheetTitle>
                <h3 className="text-xl text-gray-500">{selectedPartner?.name}</h3>
              </div>
              <Button
                className="mr-[25px] rounded-xl bg-[#3f8b98] text-white font-['Poppins',Helvetica] font-medium"
                onClick={() => handleAddUserOpen(true)}
              >
                <PlusIcon className="mr-2 h-3.5 w-3.5 " />
                Add User
              </Button>
            </div>
          </SheetHeader>

          {selectedPartner && (
            <div className="mt-6">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-neutral-200">
                    <TableHead className="w-[200px]">User Name</TableHead>
                    <TableHead className="w-[200px]">Designation</TableHead>
                    <TableHead className="w-[150px]">Mobile</TableHead>
                    <TableHead className="w-[250px]">Email</TableHead>
                    <TableHead className="w-[100px]">Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                {/* <div className="w-full max-h-[300px] overflow-y-auto"> */}
                <TableBody>
                  {selectedPartner.users.map((user, index) => (
                    <TableRow className="border-b border-neutral-200" key={index}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-[#8c650033] flex items-center justify-center">
                            <span className="text-sm">{getInitials(user.name)}</span>
                          </div>
                          <span>{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {partner_designation.find((designation) => designation.id === Number(user.designation_id))?.name || "-"}
                      </TableCell>
                      <TableCell>{user.mobile}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`rounded-full ${user.is_active
                            ? 'bg-[#3f8b981a] text-[#3f8b98]'
                            : 'bg-[#8c65001a] text-[#8c6500]'
                            } border-0 px-3 py-1 font-normal text-sm`}
                        >
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 p-0"
                            >
                              <MoreVerticalIcon className="h-4 w-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => handleEditUser(user)}>
                              Edit User
                            </DropdownMenuItem>
                            {
                              user.is_active ? (
                                <DropdownMenuItem
                                  className="text-red-600"
                                  onClick={() => handleDeactivateUser(user)}
                                >
                                  Deactivate User
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  className="text-green-600"
                                  onClick={() => handleActivateUser(user)}
                                >
                                  Activate User
                                </DropdownMenuItem>
                              )
                            }
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                {/* </div> */}
              </Table>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Partner Profile Sidebar */}

      <Sheet
        open={isProfileSheetOpen}
        onOpenChange={(open) => {
          setIsProfileSheetOpen(open);
          if (!open) {
            setTimeout(() => {
              setSelectedPartner(null);
            }, 10);
          }
        }}
      >
        <SheetContent className="w-[900px] max-w-[75vw] overflow-y-auto">
          <SheetHeader className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <SheetTitle>Your Profile</SheetTitle>
                <h3 className="text-xl text-gray-500">{selectedPartner?.name}</h3>
              </div>
            </div>
          </SheetHeader>
          {selectedPartner?.profile && (
            <div className="space-y-8">
              {/* Basic Details */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Profile Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Partner Type</Label>


                    <Select
                      defaultValue={selectedPartner?.profile?.partner_type}
                      onValueChange={handlePartnerTypeChange}
                    >
                      <SelectTrigger className="bg-white border border-gray-300 rounded-md">
                        <SelectValue placeholder="Select partner type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                        <SelectItem value="Individual" className="hover:bg-gray-100">
                          Individual
                        </SelectItem>
                        <SelectItem value="Company" className="hover:bg-gray-100">
                          Company
                        </SelectItem>
                      </SelectContent>
                    </Select>


                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_name" >Company Name</Label>
                    <Input
                      id="company_name"
                      defaultValue={selectedPartner?.profile?.company_name}
                      onChange={(e) => handleProfileInputChange('company_name', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Display Name</Label>
                    <Input defaultValue={selectedPartner.name} onChange={(e) => handledisplayname(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Zone</Label>
                    <Select
                      value={normalizeZoneValue(selectedPartner?.profile?.zone)}
                      onValueChange={handleZoneChange}
                    >
                      <SelectTrigger className="bg-white border border-gray-300 rounded-md">
                        <SelectValue placeholder="Select zone" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                        {zoneOptions.map((zone) => (
                          <SelectItem key={zone.value} value={zone.value} className="hover:bg-gray-100">
                            {zone.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>GST Number</Label>
                    <Input defaultValue={selectedPartner.profile.gst_number} onChange={(e) => handleProfileInputChange('gst_number', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Address</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label>Address Line 1</Label>
                    <Input onChange={(e) => handleProfileInputChange('address_line1', e.target.value)} defaultValue={selectedPartner.profile.address_line1} />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input onChange={(e) => handleProfileInputChange('address_city', e.target.value)} defaultValue={selectedPartner.profile.address_city} />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input onChange={(e) => handleProfileInputChange('address_state', e.target.value)} defaultValue={selectedPartner.profile.address_state} />
                  </div>
                  <div className="space-y-2">
                    <Label>Pincode</Label>
                    <Input
                      onChange={(e) => handleProfileInputChange('address_pincode', e.target.value)}
                      defaultValue={selectedPartner?.profile?.address_pincode}
                      className={pincodeError ? "border-red-500" : ""}
                    />
                    {pincodeError && <p className="text-red-500 text-sm">{pincodeError}</p>}
                  </div>
                </div>
              </div>

              {/* Upload KYC Documents */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Upload KYC Documents</h3>
                <div className="grid grid-cols-3 gap-x-6 gap-y-8">
                  {Data.map((data) => {
                    const document = getDocument(data.type, documents);
                    const isUploadingCurrentDoc = !!uploadingDocumentTypes[data.type];
                    const isBusy = isFetchingDocuments || isUploadingCurrentDoc;

                    return (
                    <div key={data.type}>
                      <DragAndDropUpload
                        onDrop={(acceptedFiles: File[]) => {
                          if (isBusy) return;
                          onDrop(acceptedFiles, data.type, data.filename);
                        }}
                        className={`h-[200px] w-full ${isBusy ? "pointer-events-none opacity-70" : ""}`}
                      >
                        {!document ? (
                          isFetchingDocuments ? (
                            <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                              <Loader2 className="h-5 w-5 animate-spin" />
                              Loading document...
                            </div>
                          ) : isUploadingCurrentDoc ? (
                            <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                              <Loader2 className="h-5 w-5 animate-spin" />
                              Uploading...
                            </div>
                          ) : (
                          <>
                            <DragAndDropIcon className="mb-3" />
                            <DragAndDropTitle className="mb-3">{data.title}</DragAndDropTitle>
                            <DragAndDropHelpText className="mb-3">
                              Drag & Drop or Click to Upload
                            </DragAndDropHelpText>
                            <DragAndDropMaxSizeText className="text-xs">
                              Max size: 5MB
                            </DragAndDropMaxSizeText>
                          </>
                          )
                        ) : (
                          <Suspense
                            fallback={
                              <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-5 w-5 animate-spin" />
                                Loading preview...
                              </div>
                            }
                          >
                            <LazyUploadPreview
                              document={document}
                              onDelete={() => {
                                if (selectedPartner?.id) {
                                  fetchALlDocuments();
                                }
                              }}
                            />
                          </Suspense>
                        )}
                      </DragAndDropUpload>
                    </div>
                    );
                  })}
                </div>

              </div>
              {/* </div> */}



              <div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold mb-4">Partner Payout</h3>
                    <div className="grid grid-cols-1 gap-4">

                      <div>
                        <Label>Payout Percentage (%)</Label>
                        <Input
                          type="number"
                          placeholder="Enter payout percentage"
                          min={0}
                          onChange={(e) => handleProfileInputChange('payout_percentage', e.target.value)}
                          defaultValue={selectedPartner.profile.payout_percentage}
                        />
                      </div>
                      <div>
                        <Label>Payout Type</Label>
                        <Select onValueChange={handlePayoutChange} defaultValue={selectedPartner.profile.payout_type}>
                          <SelectTrigger className="bg-white border border-gray-300 rounded-md">
                            <SelectValue placeholder="Select Partner type" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                            <SelectItem value="Fixed" className="hover:bg-gray-100">Fixed</SelectItem>
                            <SelectItem className="hover:bg-gray-100" value="Variable">Variable</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                    </div>

                  </div>

                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold mb-4">Partner Agreement</h3>
                    <div className="grid grid-cols-1 gap-x-6 gap-y-8">
                      {partneraggreement.map((data) => {
                        const document = getDocument(data.type, documents);
                        const isUploadingCurrentDoc = !!uploadingDocumentTypes[data.type];
                        const isBusy = isFetchingDocuments || isUploadingCurrentDoc;

                        return (
                        <div key={data.type}>
                          <DragAndDropUpload
                            onDrop={(acceptedFiles: File[]) => {
                              if (isBusy) return;
                              onDrop(acceptedFiles, data.type, data.filename);
                            }}
                            className={`h-[200px] w-full ${isBusy ? "pointer-events-none opacity-70" : ""}`}
                          >
                            {!document ? (
                              isFetchingDocuments ? (
                                <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                                  <Loader2 className="h-5 w-5 animate-spin" />
                                  Loading document...
                                </div>
                              ) : isUploadingCurrentDoc ? (
                                <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                                  <Loader2 className="h-5 w-5 animate-spin" />
                                  Uploading...
                                </div>
                              ) : (
                              <>
                                <DragAndDropIcon className="mb-3" />
                                <DragAndDropTitle className="mb-3">{data.title}</DragAndDropTitle>
                                <DragAndDropHelpText className="mb-3">
                                  Drag & Drop or Click to Upload
                                </DragAndDropHelpText>
                                <DragAndDropMaxSizeText className="text-xs">
                                  Max size: 5MB
                                </DragAndDropMaxSizeText>
                              </>
                              )
                            ) : (
                              <Suspense
                                fallback={
                                  <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    Loading preview...
                                  </div>
                                }
                              >
                                <LazyUploadPreview
                                  document={document}
                                  onDelete={() => {
                                    if (selectedPartner?.id) {
                                      fetchALlDocuments();
                                    }
                                  }}
                                />
                              </Suspense>
                            )}
                          </DragAndDropUpload>
                        </div>
                        );
                      })}
                    </div>
                  </div>


                </div>
              </div>

              <div className="float-right "><Button className="mb-8" onClick={saveProfileChanges}>Save Changes</Button></div>
            </div>
          )}
        </SheetContent>

      </Sheet>

      {/* Add Partner Modal */}
      <Dialog open={isAddPartnerOpen} onOpenChange={handleAddPartnerOpen}>
        <DialogContent className="dialog-wider max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Add New Partner</DialogTitle>
            <DialogDescription>Enter the details to add a new partner</DialogDescription>
          </DialogHeader>

          <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
            <div className="space-y-4 py-2 px-1">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData["name"] || ""}
                  onChange={handleChange}
                  placeholder="Enter partner name"
                  className={`w-full ${formErrors.name ? "border-red-500" : ""}`}
                />
                {formErrors.name && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData["email"] || ""}
                  onChange={handleChange}
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  className={`w-full ${formErrors.email ? "border-red-500" : ""}`}
                />
                {formErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile" className="text-sm font-medium">
                  Mobile Number
                </Label>
                <Input
                  value={formData["mobile"] || ""}
                  onChange={handleChange}
                  id="mobile"
                  type="tel"
                  placeholder="Enter mobile number"
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Designation
                </Label>
                <Select
                  defaultValue={formData["designation_id"] || ""}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      designation_id: value,
                    }))
                  }
                >
                  <SelectTrigger className="w-full bg-white border border-gray-300 rounded-md">
                    <SelectValue placeholder="Select Designation" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                    {partner_designation.map((option) => (
                      <SelectItem className="hover:bg-gray-100" key={option.id} value={String(option.id)}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Product <span className="text-red-500">*</span>
                </Label>
                <Select
                  defaultValue={formData["product"] || ""}
                  onValueChange={(value) => {
                    setFormData((prev) => ({
                      ...prev,
                      product: value,
                    }));
                    if (formErrors.product) {
                      setFormErrors(prev => ({
                        ...prev,
                        product: ""
                      }));
                    }
                  }}
                >
                  <SelectTrigger
                    className={`w-full bg-white border rounded-md ${formErrors.product ? "border-red-500" : "border-gray-300"}`}
                  >
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent className="w-full bg-white border border-gray-300 rounded-md shadow-md">
                    <SelectItem value="Hybrid" className="hover:bg-gray-100">
                      Hybrid
                    </SelectItem>
                    <SelectItem value="Leasing" className="hover:bg-gray-100">
                      Leasing
                    </SelectItem>
                  </SelectContent>
                </Select>
                {formErrors.product && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.product}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="mca" className="text-sm font-medium">
                  MCA Registered Entity Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData["mca"] || ""}
                  onChange={handleChange}
                  id="mca"
                  placeholder="Enter MCA registered name"
                  className={`w-full ${formErrors.mca ? "border-red-500" : ""}`}
                />
                {formErrors.mca && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.mca}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="org" className="text-sm font-medium">
                  Organisation Name
                </Label>
                <Input
                  value={formData["org"] || ""}
                  onChange={handleChange}
                  id="org"
                  placeholder="Enter organisation name"
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 mt-2 border-t flex-shrink-0">
            <Button variant="outline" onClick={() => handleAddPartnerOpen(false)}>
              Cancel
            </Button>
            <Button onClick={addpartner}>Add Partner</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add User Modal */}
      <Dialog open={isAddUserOpen} onOpenChange={handleAddUserOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Enter the details to add a new user</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="userName">Name</Label>
              <Input value={addUserData["userName"]}
                onChange={handleChangeadduser} id="userName" placeholder="Enter user name" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="userEmail">Email</Label>
              <Input value={addUserData["userEmail"]}
                onChange={handleChangeadduser} id="userEmail" type="email" placeholder="Enter email address" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="userMobile">Mobile Number</Label>
              <Input value={addUserData["userMobile"]}
                onChange={handleChangeadduser} id="userMobile" type="tel" placeholder="Enter mobile number" />
            </div>



            <div className="space-y-2">
              <Label >Designation</Label>
              <Select
                defaultValue={addUserData["userDesignation_id"]}
                onValueChange={(value) =>
                  setAddUserData((prev) => ({
                    ...prev,
                    userDesignation_id: value,
                  }))
                }
              >
                <SelectTrigger className="bg-white border border-gray-300 rounded-md">
                  <SelectValue placeholder="Select Designation" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                  {partner_designation.map((option) => (
                    <SelectItem className="hover:bg-gray-100" key={option.id} value={String(option.id)}>
                      {option.name}
                    </SelectItem>
                  ))}

                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={() => setIsAddUserOpen(false)}>
              Cancel
            </Button>
            <Button onClick={addpartneruser} >Add User</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={isEditUserOpen} onOpenChange={setIsEditUserOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user details</DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="editUserName">Name</Label>
                <Input
                  id="editUserName"
                  defaultValue={selectedUser.name}
                  onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="editUserEmail">Email</Label>
                <Input
                  id="editUserEmail"
                  type="email"
                  defaultValue={selectedUser.email}
                  onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="editUserMobile">Mobile Number</Label>
                <Input
                  id="editUserMobile"
                  type="tel"
                  defaultValue={selectedUser.mobile}
                  onChange={(e) => setSelectedUser({ ...selectedUser, mobile: e.target.value })}
                />
              </div>

              {/* <div className="space-y-2">
                <Label htmlFor="editUserDesignation">Designation</Label>
                <Input
                  id="editUserDesignation"
                  defaultValue={selectedUser.designation}
                  onChange={(e) => setSelectedUser({ ...selectedUser, designation: e.target.value })}
                />
              </div> */}

              <div className="space-y-2">
                <Label >Designation</Label>
                <Select
                  defaultValue={String(selectedUser.designation_id)} // Ensure the default value is a string
                  onValueChange={(value) =>
                    setSelectedUser((prev) => ({
                      ...prev!,
                      designation_id: value,
                    }))
                  }
                >
                  <SelectTrigger className="bg-white border border-gray-300 rounded-md">
                    <SelectValue placeholder="Select Designation">
                      {
                        partner_designation.find(
                          (option) => String(option.id) === String(selectedUser.designation_id)
                        )?.name || "Select Designation"
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-300 rounded-md shadow-md">
                    {partner_designation.map((option) => (
                      <SelectItem
                        className="hover:bg-gray-100"
                        key={option.id}
                        value={String(option.id)}
                      >
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

            </div>
          )}

          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={() => setIsEditUserOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleUpdateUser(selectedUser!)}>Update User</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
